import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { TwoFACode } from '@app/auth/core/state/auth.model';
import { BackupCode, Identity } from '@app/auth/models';
import { Credentials, LoginState } from '@app/auth/routes/login/state';
import { Configure2FA } from '@app/auth/core/state/auth.reducer';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { selectRecoveryCodes } from '@app/auth/core/state/auth.selectors';
import { UserState } from '@app/user/core/state/user.reducer';
import { TwoFactorSetupService } from '@app/shared/services/two-factor-setup.service';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tc-login',
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TwoFactorSetupService],
})
export class LoginComponent implements OnDestroy {
  @Input()
  identity?: Identity;

  @Input()
  loginState?: LoginState;

  @Input()
  configure2FA!: Configure2FA;

  @Output()
  readonly loginUser = new EventEmitter<Credentials>();

  @Output()
  readonly checkEmailDomain = new EventEmitter<string>();

  @Output()
  readonly verify2FA = new EventEmitter<TwoFACode>();

  @Output()
  readonly verify2FAViaBackup = new EventEmitter<BackupCode>();

  @Output()
  readonly enable2FA = new EventEmitter<TwoFACode>();

  @Output()
  readonly backToLogin = new EventEmitter<TwoFACode>();

  @Output()
  readonly clearRecovery = new EventEmitter();

  public backupCodes$: Observable<string[]> = this.store$.pipe(select(selectRecoveryCodes));

  public readonly shouldVerifyWithBackupCode$ = new BehaviorSubject<'initial' | 'verification' | 'backup'>(
    'verification',
  );
  private destroyed$ = new Subject<void>();

  constructor(private store$: Store<UserState>, private twoFService: TwoFactorSetupService) {
    this.initTwoFASetup();
  }

  onBackToLogin(): void {
    this.backToLogin.emit();
  }

  onBackToVerification(): void {
    this.shouldVerifyWithBackupCode$.next('verification');
  }

  public onSwitchToFormWithBackup(): void {
    this.shouldVerifyWithBackupCode$.next('backup');
  }

  initTwoFASetup(): void {
    this.twoFService.backupCodes$
      .pipe(
        map(codes => {
          return !!codes?.length;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(value => {
        this.twoFService.emitLastStep(value);
      });

    this.twoFService.onReset$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.clearRecovery.emit();
    });

    this.twoFService.onEnable$.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.enable2FA.emit(value);
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
